/* eslint-disable react-hooks/exhaustive-deps */
import React, { MouseEvent, useEffect, useRef, useState } from "react";
import { useServiceContext } from "../../contexts/service";
import { TChatMessage, TChatResponse } from "../../lib/types/chats";
import { useAuthContext } from "../../contexts/auth-context";
import { getNewChatMessages } from "../../services/notifications";
import { ChatList } from "./chatList";

type TMessageProps = {
    data: TChatMessage;
    currentUserGUID: string;
};

type TChatComposerProps = {
    inputRef: React.Ref<HTMLInputElement>;
    focusHandler: (isFocused: boolean) => void;
    sendHandler: (e: MouseEvent) => void;
    sendMessageHandler: () => void;
};

type TChatHeaderProps = {
    fullScreenMode: boolean;
    withUserName: string;
    chatState: TChatState;
    newMessagesCount: number;
    titleBarRef: React.Ref<HTMLDivElement>;
    onClickMinimizeChatHandler: (e: React.MouseEvent) => void;
    oncClickCloseChatHandler: (e: React.MouseEvent) => void;
};

export const ChatMessage = ({ data, currentUserGUID: currentUserId }: TMessageProps) => {
    const messageClassName = data.state === "unread" ? "message-unread" : "message-read";
    const itsMe = data.from_user_guid === currentUserId;
    const contentAlign = `wwg-chat-message d-flex px-3 mb-3 ${itsMe ? "justify-content-end" : "justify-content-start"}`;
    const messageClass = `wwg-chat-message__bubble d-flex flex-column rounded-2 py-2 px-3  ${itsMe ? "chatItsMe" : "chatNotMe"}`;
    return (
        <div className={contentAlign}>
            <div className={messageClass}>
                <div className="wwg-chat-message__text">{data.content}</div>
                {itsMe && (
                    <div className="align-self-end lh-1">
                        <i className={`fa-solid fa-check-double ${messageClassName}`}></i>
                    </div>
                )}
            </div>
        </div>
    );
};

const ChatComposer = ({ inputRef, focusHandler, sendHandler, sendMessageHandler }: TChatComposerProps) => {
    return (
        <div className="chatComposer bg-light row g-2 g-sm-3 align-items-center p-2 ">
            <div className="col m-0">
                <input
                    onFocus={() => focusHandler(true)}
                    onBlur={() => focusHandler(false)}
                    onKeyUp={(e) => {
                        if (e.key.toLowerCase() === "enter") {
                            sendMessageHandler();
                        }
                    }}
                    ref={inputRef}
                    type="text"
                    placeholder="Write your message"
                    className="form-control rounded-pill px-3 shadow-none chatComposer__input"
                />
            </div>
            <div className="col-auto m-0">
                <button className="btn btn-primary rounded-2 p-0 chatComposer__send" onClick={sendHandler} aria-label="Send message">
                    <i className="fa-regular fa-paper-plane"></i>
                </button>
            </div>
        </div>
    );
};

const ChatHeader = ({
    fullScreenMode,
    withUserName,
    chatState,
    newMessagesCount,
    titleBarRef,
    onClickMinimizeChatHandler,
    oncClickCloseChatHandler,
}: TChatHeaderProps) => {
    return (
        <div ref={titleBarRef} onClick={onClickMinimizeChatHandler} className="chatTitle row g-0 align-items-center bg-secondary rounded-top-3 text-white px-3">
            <div className="col-auto">
                {fullScreenMode ? (
                    <button
                        className="btn border-0 text-white p-0 rounded-3 chatTitle__iconButton"
                        onClick={oncClickCloseChatHandler}
                        aria-label="Back to conversations"
                    >
                        <i className="fa-chevron-left fa-solid"></i>
                    </button>
                ) : (
                    <span className="chatTitle__iconSpacer chatTitle__minimizeHint" aria-hidden="true">
                        <i className="fa-solid fa-minus"></i>
                    </span>
                )}
            </div>
            <h6 className={`chatTitle__text col text-center lh-sm px-3 mb-0 ${chatState === "minimized" ? "chatTitle__text--minimized" : ""}`}>
                <span className="d-inline-flex align-items-center justify-content-center mw-100">
                    <strong>{withUserName}</strong>
                </span>
                {chatState === "minimized" && newMessagesCount > 0 && <span className="badge bg-primary ms-2">{newMessagesCount}</span>}
            </h6>
            <div className="col-auto text-end">
                {!fullScreenMode ? (
                    <button className="btn border-0 text-white p-0 rounded-3 chatTitle__iconButton" onClick={oncClickCloseChatHandler} aria-label="Close chat">
                        <i className="fa-close fa-solid"></i>
                    </button>
                ) : (
                    <span className="chatTitle__iconSpacer" aria-hidden="true" />
                )}
            </div>
        </div>
    );
};

type TEmergentChatProps = {
    withUserId: number;
    withUserName: string;
    chatIdx: number;
    fullScreenMode?: boolean;
};
type TChatProps = {
    withUserId: number;
    withUserName: string;
    focusHandler: (isFocused: boolean) => void;
};
export const Chat = ({ withUserId, focusHandler }: TChatProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const divConversationRef = useRef<HTMLDivElement>(null);

    const {
        chats: { sendMessage, startConversation, getMessages },
        notifications: { addNewConversationMessageHandler, removeNewConversationMessageHandler, setReadNotificationMessageHandler },
    } = useServiceContext();
    const { userInfo } = useAuthContext();
    const [allowChat, setAllowChat] = useState<boolean>(true);
    const [chatInfo, setChatInfo] = useState<TChatResponse>({
        data: {
            messages: [],
            conversationId: -1,
        },
    });

    const _sendMessage = async () => {
        if (!inputRef) return;
        if (!inputRef.current?.value) return;
        if (!inputRef.current.value.trim()) return;
        const message = inputRef.current.value;
        inputRef.current.value = "";
        await sendMessage(message, chatInfo.data.conversationId);
        //hack to force the update of messages
        setChatInfo((prev) => ({ ...prev }));
    };

    const sendHandler = async (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        _sendMessage();
    };

    const scrollToBottom = () => {
        setTimeout(() => {
            if (!divConversationRef.current) {
                scrollToBottom();
            } else {
                divConversationRef.current.scrollTop = divConversationRef.current.scrollHeight;
            }
        }, 100);
    };

    useEffect(() => {
        clearTimeout((window as any).wwgchatsAutomaticUpdateMessage);
        startConversation(withUserId)
            .then((data) => {
                setChatInfo(data);
                scrollToBottom();
            })
            .catch((err) => {
                console.error(err);
                setAllowChat(false);
            });
        return () => {
            clearTimeout((window as any).wwgchatsAutomaticUpdateMessage);
        };
    }, [withUserId]);

    useEffect(() => {
        if (!chatInfo) return;
        if (!chatInfo.data) return;

        const updateMessages = async (conversationId: number, lastMessageDateNoTZ: string) => {
            const data = await getMessages(conversationId, lastMessageDateNoTZ);
            if (data.data.messages.length > 0) {
                //-//clearTimeout((window as any).wwgchatsAutomaticUpdateMessage);

                setChatInfo((prev) => {
                    const newData = { ...prev };
                    newData.data.messages = [...newData.data.messages, ...data.data.messages];
                    return newData;
                });
                scrollToBottom();
            }
        };

        let dateFilter = "";
        if (chatInfo.data.messages && chatInfo.data.messages.length > 0) {
            dateFilter = chatInfo.data.messages[chatInfo.data.messages.length - 1].sent_date;
        }

        const _newConversationMessageHandler = (pConversationId: number) => {
            if (pConversationId !== chatInfo.data.conversationId) return;
            updateMessages(chatInfo.data.conversationId, dateFilter);
        };

        addNewConversationMessageHandler(_newConversationMessageHandler);
        setReadNotificationMessageHandler((p) => {
            if (chatInfo.data.conversationId !== p.conversationId) return;
            const indexStart = chatInfo.data.messages.findIndex((m) => m.sent_date === p.fromDate);
            const indexEnd = chatInfo.data.messages.findIndex((m) => m.sent_date === p.toDate);
            for (let i = indexStart; i <= indexEnd; i++) {
                const chatState = chatInfo.data.messages[i];
                if (chatState && chatState.state) chatState.state = "read";
                setChatInfo({ ...chatInfo });
            }
        });
        return () => {
            removeNewConversationMessageHandler(_newConversationMessageHandler);
            setReadNotificationMessageHandler(undefined);
        };
    }, [chatInfo]);

    return (
        <div className="d-flex flex-column flex-grow-1 min-h-0 h-100">
            <div ref={divConversationRef} className={`m-0 chatContentStyle ${allowChat ? "py-2" : ""} ${allowChat ? "chatContentStyle__minHeight" : ""}`}>
                {chatInfo.data.messages.map((m) => (
                    <ChatMessage key={m.sent_date} currentUserGUID={userInfo?.guid as string} data={m} />
                ))}
            </div>
            {allowChat && <ChatComposer inputRef={inputRef} focusHandler={focusHandler} sendHandler={sendHandler} sendMessageHandler={_sendMessage} />}
            {!allowChat && (
                <div className="bg-light">
                    <div className="p-2 text-black-50">
                        {" "}
                        <small>User is not in your contacts.</small>
                    </div>
                </div>
            )}
        </div>
    );
};
type TChatState = "expanded" | "minimized";

export const ChatEmergentChat = ({ withUserId, withUserName, chatIdx, fullScreenMode = false }: TEmergentChatProps) => {
    const [chatState, setChatState] = useState<TChatState>("expanded");

    const {
        notifications: { addNewMessagesHandler, removeMessageHandler },
        chats: { endConversation, removeUserIdToChat },
    } = useServiceContext();

    const oncClickCloseChatHandler = (e: React.MouseEvent) => {
        e.stopPropagation();
        removeUserIdToChat(withUserId);
        endConversation(withUserId);
    };

    const onClickMinimizeChatHandler = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (fullScreenMode) return;
        setChatState((old) => {
            const newState = old === "expanded" ? "minimized" : "expanded";
            if (newState === "minimized") {
                endConversation(withUserId);
                addNewMessagesHandler(checkNewMessages);
            } else {
                removeMessageHandler(checkNewMessages);
            }

            return newState;
        });
    };

    const { getUserTokenId } = useAuthContext();

    const [newMessagesCount, setNewMessagesCount] = useState<number>(0);

    const checkNewMessages = async () => {
        const userTokenId = await getUserTokenId();
        if (!userTokenId) {
            setTimeout(() => {
                checkNewMessages();
            }, 5000);
            return;
        }
        const messages = await getNewChatMessages(userTokenId, withUserId);
        if (messages && messages.data && messages.data.messages && messages.data.messages.length > 0) {
            setNewMessagesCount(Number(messages.data.messages[0].new_messages));
        } else {
            setNewMessagesCount(0);
        }
    };

    const titleBarRef = useRef<HTMLDivElement>(null);

    const focusHandler = (isFocused: boolean) => {
        if (titleBarRef.current) {
            if (isFocused || fullScreenMode) {
                titleBarRef.current.classList.remove("bg-secondary-disabled");
            } else {
                titleBarRef.current.classList.add("bg-secondary-disabled");
            }
        }
    };

    const _style: React.CSSProperties = {
        right: fullScreenMode ? "" : `${(chatIdx + 1) * 25}rem`,
    };

    useEffect(() => {
        if (chatState === "minimized") checkNewMessages();
        if (fullScreenMode) focusHandler(true);

        return () => {
            removeMessageHandler(checkNewMessages);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <div
            style={_style}
            className={`shadow-lg chatStyle rounded-top-3 ${fullScreenMode ? "chatStyle--fullscreen" : ""} ${
                chatState === "minimized" ? "chatStyle--minimized" : ""
            }`}
        >
            <ChatHeader
                fullScreenMode={fullScreenMode}
                withUserName={withUserName}
                chatState={chatState}
                newMessagesCount={newMessagesCount}
                titleBarRef={titleBarRef}
                onClickMinimizeChatHandler={onClickMinimizeChatHandler}
                oncClickCloseChatHandler={oncClickCloseChatHandler}
            />
            {chatState === "expanded" && (
                <div className="chatStyle__body">
                    <Chat focusHandler={focusHandler} withUserId={withUserId} withUserName={withUserName} />
                </div>
            )}
        </div>
    );
};

export const ChatOpenChatList = () => {
    const {
        chats: { listUserIdToChat },
    } = useServiceContext();

    return (
        <>
            {listUserIdToChat.map((uitc, idx) => (
                <>
                    <ChatEmergentChat chatIdx={idx} key={"chat_list_uitc_" + uitc.id} withUserId={uitc.id} withUserName={uitc.name} />
                </>
            ))}
            <ChatList />
        </>
    );
};

export const MobileOpenChatList = () => {
    const {
        chats: { listUserIdToChat },
    } = useServiceContext();
    return (
        <>
            {listUserIdToChat.map((u, idx) => (
                <ChatEmergentChat fullScreenMode={true} chatIdx={idx} key={"chat_list_uitc_" + u.id} withUserId={u.id} withUserName={u.name} />
            ))}
            <ChatList fullScreenMode={true} />
        </>
    );
};
